import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { gql, useMutation, useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { DATE_YEAR_MONTH_DAY_DASH_TIME_HOUR_MINUTE } from '../../utils/dates';

import { convertGramsToLargeUnits, convertLargeUnitsToGrams, weightLargeUnitLabel } from '../../utils/unitConversion';
import useUser from '../../utils/hooks/useUser';
import Button from '../../atoms/Button';
import Select from '../../atoms/Select';
import FeedFloTextInput from '../../atoms/FeedFloTextInput';
import NumberInput from '../../atoms/NumberInput';
import TextArea from '../../atoms/TextArea';
import { SourceEnum } from '../../organisms/BinCheckDialog/enums';
import { purposeList, methodList } from '../../organisms/BinCheckDialog/lists';
import { Formik } from 'formik';
import { DeleteBinSetLevelMutation, InsertBinSetLevelMutation, UpdateBinSetLevelMutation } from './queries';
import { toast } from 'react-toastify';
import ConfirmationDialog from '../ConfirmationDialog';
import { binCheckSchema } from './validation';
import FeedFloToolTip from '../../atoms/FeedFloToolTip';
import { InfoIcon } from '../../atoms/Icons';

const CALIBRATION_STATUS_MAP = {
  ignored: 'Not used for Calibration',
  calculated: 'Calibrated',
  calculating: 'Calibrating',
  recalculating: 'Re-calibrating',
  unknown: 'Unknown',
  error: 'Error - Contact Support',
};

function BinCheckDataPane({ binSetLevelId, binSetId, isEditing = true, onClose = () => { } }) {
  const { user } = useUser();
  const isMetric = user?.isMetric || false;
  const deleteConfirmationDialogRef = useRef(null);

  const [insertBinSetLevel] = useMutation(InsertBinSetLevelMutation);
  const [updateBinSetLevel] = useMutation(UpdateBinSetLevelMutation, {
    refetchQueries: ['BinCheckDataPane_BinLevel', 'BinCalibration_BinSetLevelQuery'],
  });
  const [deleteBinSetLevel] = useMutation(DeleteBinSetLevelMutation);

  const { loading, data } = useQuery(
    gql`
      query BinCheckDataPane_BinLevel($binSetId: uuid!) {
        bin_set_by_pk(id: $binSetId) {
          id
          bins(where: { deleted_at: { _is_null: true } }, order_by: { name: asc }) {
            id
            name
          }
        }
      }
    `,
    {
      variables: {
        binSetId: binSetId,
      },
      fetchPolicy: 'network-only',
    },
  );

  const { loading: loadingExisting, data: existingData } = useQuery(
    gql`
      query BinCheckDataPane_ExistingBinLevel($id: uuid!) {
        bin_set_level_by_pk(id: $id) {
          id
          bin_set_id
          valid_at
          level_in_grams
          source
          method
          purpose
          comment
          bin_levels_in_grams
          calibration_status
          created_at
          updated_at
          bin_set {
            id
            bins(where: { deleted_at: { _is_null: true } }, order_by: { name: asc }) {
              id
              name
            }
          }
        }
      }
    `,
    {
      variables: {
        id: binSetLevelId,
      },
      skip: !binSetLevelId,
      fetchPolicy: 'network-only',
    },
  );

  const calculateErrorInGrams = (method, binLevels, bins) => {
    const errorRate = convertLargeUnitsToGrams(isMetric, 1.5); // Every method other than empty has a hardcoded rate of 1.5 for now
    const emptyBinCount = Object.values(binLevels).filter((level) => level === 0).length;
    const binCount = bins.length;

    return (binCount - emptyBinCount) * errorRate;
  };

  const onDeleteConfirmationCancelClick = () => {
    if (!deleteConfirmationDialogRef?.current) {
      return;
    }

    deleteConfirmationDialogRef.current.close();
  };

  const onDeleteConfirmationConfirmClick = () => {
    deleteBinSetLevel({
      variables: {
        id: existingData.bin_set_level_by_pk.id,
        deleted_at: dayjs().unix(),
      },
      refetchQueries: [
        'BinCalibration_BinSetLevelQuery',
        'CalibrationCard_GetBinSetCalibrations',
        'CalibrationCard_MarkerData'
      ],
      onCompleted: () => {
        toast('Bin Check Deleted', {
          position: 'top-right',
          autoClose: 3000,
          closeOnClick: true,
        });
        onClose();
      },
    });
  };

  const onDelete = () => {
    if (!deleteConfirmationDialogRef?.current) {
      return;
    }

    deleteConfirmationDialogRef.current.showModal();
  };

  const existingBinSetCalibration = existingData?.bin_set_level_by_pk;
  const binSetData = existingBinSetCalibration?.bin_set || data?.bin_set_by_pk;
  const initialValues = {
    purpose: existingBinSetCalibration?.purpose,
    validAtText: existingBinSetCalibration?.valid_at
      ? dayjs(existingBinSetCalibration?.valid_at * 1000).format(DATE_YEAR_MONTH_DAY_DASH_TIME_HOUR_MINUTE)
      : dayjs().format(DATE_YEAR_MONTH_DAY_DASH_TIME_HOUR_MINUTE),
    source: existingBinSetCalibration?.source,
    method: existingBinSetCalibration?.method,
    comment: existingBinSetCalibration?.comment,
    binLevel_total: convertGramsToLargeUnits(isMetric, existingBinSetCalibration?.level_in_grams),
  };

  // Add initial values for each bin's level
  if (binSetData?.bins) {
    binSetData.bins.forEach((bin) => {
      const binLevelInGrams = existingBinSetCalibration?.bin_levels_in_grams?.[bin.id];
      initialValues[`binLevel_${bin.id}`] =
        typeof binLevelInGrams === 'number' ? convertGramsToLargeUnits(isMetric, binLevelInGrams) : '';
    });
  }

  const missingPerBinLevels = binSetData?.bins?.some((bin) => {
    return typeof existingBinSetCalibration?.bin_levels_in_grams?.[bin.id] !== 'number';
  });

  const onSubmit = (values) => {
    const binLevelsInGrams = {};
    let totalLevelInGrams = convertLargeUnitsToGrams(isMetric, values['binLevel_total']);

    // Convert each bin's level to grams and calculate total
    const hasAllBinLevels = binSetData?.bins.every((bin) => {
      const value = values[`binLevel_${bin.id}`];
      return value !== undefined && value !== '' && !isNaN(Number(value));
    });
    if (hasAllBinLevels) {
      totalLevelInGrams = 0;
      binSetData?.bins.forEach((bin) => {
        const binLevel = values[`binLevel_${bin.id}`];
        if (binLevel !== undefined && binLevel !== '') {
          const levelInGrams = Math.round(convertLargeUnitsToGrams(isMetric, Number(binLevel)));
          binLevelsInGrams[bin.id] = levelInGrams;
          totalLevelInGrams += levelInGrams;
        }
      });
    }

    if (isEditing) {
      updateBinSetLevel({
        variables: {
          id: existingData.bin_set_level_by_pk.id,
          valid_at: dayjs(values.validAtText).unix(),
          level_in_grams: Math.round(totalLevelInGrams),
          bin_set_id: existingData.bin_set_level_by_pk.bin_set_id,
          source: SourceEnum.Farmer,
          method: values.method,
          purpose: values.purpose,
          expected_deviation_in_grams: calculateErrorInGrams(values.method, binLevelsInGrams, binSetData?.bins),
          comment: values.comment,
          bin_levels_in_grams: binLevelsInGrams,
        },
        refetchQueries: [
          'BinCalibration_BinSetLevelQuery',
          'CalibrationCard_GetBinSetCalibrations',
          'CalibrationCard_MarkerData'
        ],
        onCompleted: () => {
          toast('Bin Check Updated', {
            position: 'top-right',
            autoClose: 3000,
            closeOnClick: true,
          });
          onClose();
        },
      });
    } else {
      insertBinSetLevel({
        variables: {
          object: {
            valid_at: dayjs(values.validAtText).unix(),
            level_in_grams: Math.round(totalLevelInGrams),
            bin_set_id: binSetId,
            source: SourceEnum.Farmer,
            method: values.method,
            purpose: values.purpose,
            expected_deviation_in_grams: calculateErrorInGrams(values.method, binLevelsInGrams, binSetData?.bins),
            comment: values.comment,
            bin_levels_in_grams: binLevelsInGrams,
          },
        },
        refetchQueries: [
          'BinCalibration_BinSetLevelQuery',
          'CalibrationCard_GetBinSetCalibrations',
          'CalibrationCard_MarkerData'
        ],
        onCompleted: () => {
          toast('Bin Check Inserted', {
            position: 'top-right',
            autoClose: 3000,
            closeOnClick: true,
          });
          onClose();
        },
      });
    }
  };

  return (
    <>
      <div className="BinCheckDataPane-body">
        <Formik
          enableReinitialize={true}
          validationSchema={binCheckSchema}
          initialValues={initialValues}
          onSubmit={onSubmit}
        >
          {({ handleSubmit, setFieldValue, values, errors }) => {
            const onPurposeChange = (e) => {
              setFieldValue('purpose', e.target.value);
            };

            const onValidAtChanged = (e) => {
              setFieldValue('validAtText', e);
            };

            const onMethodChange = (e) => {
              setFieldValue('method', e.target.value);
            };

            const onCommentChange = (e) => {
              setFieldValue('comment', e.target.value);
            };

            const onBinLevelChange = (binId, e) => {
              setFieldValue(`binLevel_${binId}`, e.target.value);
            };

            const binLevelValues = binSetData?.bins
              .map((bin) => {
                return values[`binLevel_${bin.id}`];
              })
              .join(',');

            useEffect(() => {
              const hasAllBinLevels = binSetData?.bins.every((bin) => {
                const value = values[`binLevel_${bin.id}`];
                return value !== undefined && value !== '' && !isNaN(Number(value));
              });
              if (hasAllBinLevels || !missingPerBinLevels || !isEditing) {
                const totalLevel = binSetData?.bins.reduce(
                  (sum, bin) => sum + (Number(values[`binLevel_${bin.id}`]) || 0),
                  0,
                );
                if (binSetData?.bins) {
                  setFieldValue('binLevel_total', totalLevel.toFixed(2));
                }
              }
            }, [binLevelValues]);

            return (
              <>
                {existingBinSetCalibration && (
                  <div className="form-group">
                    <label>Calibration Status</label>
                    <div className="form-control">
                      {existingBinSetCalibration.purpose === 'reset'
                        ? 'Bin Level Reset - Calibration maintained'
                        : CALIBRATION_STATUS_MAP[existingBinSetCalibration?.calibration_status] ||
                        CALIBRATION_STATUS_MAP.unknown}
                    </div>
                  </div>
                )}
                <Select
                  inputRef={null}
                  disabled={loading || loadingExisting}
                  label="Purpose"
                  defaultText="Select a purpose"
                  itemList={purposeList}
                  value={values.purpose}
                  onChange={onPurposeChange}
                  isValid={!errors?.purpose}
                  description={errors?.purpose}
                />
                <FeedFloTextInput
                  type="datetime-local"
                  label="Checked At"
                  disabled={loading || loadingExisting}
                  text={values.validAtText}
                  onChange={onValidAtChanged}
                  isValid={!errors?.validAtText}
                  description={errors?.validAtText}
                />
                <Select
                  label="Method"
                  defaultText="Select a method"
                  itemList={methodList}
                  disabled={loading || loadingExisting}
                  value={values?.method}
                  onChange={onMethodChange}
                  isValid={!errors?.method}
                  description={errors?.method}
                />

                {binSetData?.bins.map((bin) => (
                  <NumberInput
                    key={bin.id}
                    label={`Bin ${bin.name} Level (in ${weightLargeUnitLabel(isMetric)}s)`}
                    value={values[`binLevel_${bin.id}`]}
                    min={0.0}
                    max={250}
                    step={0.01}
                    onChange={(e) => onBinLevelChange(bin.id, e)}
                    disabled={loading || loadingExisting}
                    isValid={!errors?.[`binLevel_${bin.id}`]}
                    description={errors?.[`binLevel_${bin.id}`]}
                  />
                ))}

                {!missingPerBinLevels || !isEditing ? (
                  <div className="form-group">
                    <label>Total Level (in {weightLargeUnitLabel(isMetric)}s)</label>
                    <div className="form-control">{values[`binLevel_total`] || '0.00'}</div>
                  </div>
                ) : (
                  <NumberInput
                    key="totalLevel"
                    label={`Total Level (in ${weightLargeUnitLabel(isMetric)}s)`}
                    value={values[`binLevel_total`]}
                    min={0.0}
                    max={250}
                    step={0.01}
                    onChange={(e) => setFieldValue(`binLevel_total`, e.target.value)}
                    disabled={loading || loadingExisting}
                    isValid={!errors?.[`binLevel_total`]}
                    description={errors?.[`binLevel_total`]}
                  />
                )}
                <TextArea
                  label="Notes"
                  value={values?.comment}
                  onChange={onCommentChange}
                  disabled={loading || loadingExisting}
                />
                <FeedFloToolTip
                  description={`Created at: ${dayjs(existingBinSetCalibration?.created_at * 1000).format(
                    DATE_YEAR_MONTH_DAY_DASH_TIME_HOUR_MINUTE,
                  )} Updated at: ${dayjs(existingBinSetCalibration?.updated_at * 1000).format(
                    DATE_YEAR_MONTH_DAY_DASH_TIME_HOUR_MINUTE,
                  )} `}
                  multiline={false}
                >
                  <InfoIcon />
                </FeedFloToolTip>

                <div className="RightSidebar-footer">
                  <Button
                    content="Cancel"
                    onClick={() => {
                      onClose();
                    }}
                  />
                  <Button
                    loading={loading || loadingExisting}
                    disabled={loading || loadingExisting}
                    variant="vivid"
                    color="success"
                    content={isEditing ? 'Update' : 'Create'}
                    onClick={handleSubmit}
                  />
                  {isEditing && (
                    <Button
                      loading={loading || loadingExisting}
                      disabled={loading || loadingExisting}
                      variant="pastel"
                      color="danger"
                      content={'Delete'}
                      onClick={onDelete}
                    />
                  )}
                </div>
                <ConfirmationDialog
                  dialogRef={deleteConfirmationDialogRef}
                  message={'Are you sure you want to delete\nthis Bin Set Level?'}
                  onCancel={onDeleteConfirmationCancelClick}
                  onConfirm={onDeleteConfirmationConfirmClick}
                />
              </>
            );
          }}
        </Formik>
      </div>
    </>
  );
}
BinCheckDataPane.propTypes = {
  binSetLevelId: PropTypes.string,
  binSetId: PropTypes.string,
  onClose: PropTypes.func,
  isEditing: PropTypes.bool,
  numBins: PropTypes.number,
};

export default BinCheckDataPane;
